import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { SectionHeader } from '../../components/SectionHeader';
import { StatusChip } from '../../components/StatusChip';
import { reasonMessage } from '../../i18n';
import type { ApiError } from '../../lib/api/api-client';

import { ReasonAlert } from './ReasonAlert';
import { type AccountSummary, type AccountView, formatConsentInstant } from './account-model';

/** What the account section shows: the `GET /trading-account` read, then its view. */
export type AccountSectionView =
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed'; readonly error: ApiError }
  | AccountView;

/** Props of {@link TradingAccountSection}. */
export interface TradingAccountSectionProps {
  readonly view: AccountSectionView;
  /** Opens the disconnect sheet; nothing is sent yet (S8 R8). */
  readonly onDisconnect: () => void;
  /** Opens E4: connect, finish or reconnect (S8 R9, R10). */
  readonly onConnect: () => void;
  /** Reads `GET /trading-account` again (S8 R21). */
  readonly onRetry: () => void;
}

function Summary({ summary }: { readonly summary: AccountSummary }): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const consent =
    summary.consentAt === null ? null : formatConsentInstant(summary.consentAt, language);
  return (
    <Stack spacing={0.5}>
      <Typography sx={{ fontFamily: NUMERIC_FONT, fontWeight: 600 }}>
        {t('account.tradingAccount.summary', { platform: summary.platform, last4: summary.last4 })}
      </Typography>
      <Typography sx={{ color: 'text.secondary' }}>
        {t('account.tradingAccount.details', {
          server: summary.server,
          holder: summary.holder,
          currency: summary.currency,
        })}
      </Typography>
      {summary.isDemo && (
        <Box>
          <StatusChip tone="info">{t('account.tradingAccount.demo')}</StatusChip>
        </Box>
      )}
      {consent !== null && (
        <Typography sx={{ fontFamily: NUMERIC_FONT, fontSize: 14 }}>
          {t('account.tradingAccount.consent', consent)}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * The trading account section of E11 (S8 R7 to R10, R21): the masked summary and the consent date
 * (Q81), or no account, or a connection to finish (S6 owns E4); Disconnect on a connected or
 * unreachable account only (Q83).
 */
export function TradingAccountSection({
  view,
  onDisconnect,
  onConnect,
  onRetry,
}: TradingAccountSectionProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const unreachable = reasonMessage('TRADING_ACCOUNT_UNREACHABLE');
  return (
    <Box component="section" aria-labelledby={headingId}>
      <SectionHeader id={headingId} title={t('account.tradingAccount.heading')} />
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
          {view.kind === 'loading' && <Typography>{t('account.loading')}</Typography>}
          {view.kind === 'failed' && (
            <>
              <ReasonAlert error={view.error} />
              <Button variant="outlined" onClick={onRetry}>
                {t('account.retry')}
              </Button>
            </>
          )}
          {view.kind === 'none' && (
            <>
              <Typography>{t('account.tradingAccount.none')}</Typography>
              <Button variant="contained" onClick={onConnect}>
                {t('account.tradingAccount.connect')}
              </Button>
            </>
          )}
          {view.kind === 'pending' && (
            <>
              <Typography>{t('account.tradingAccount.pending')}</Typography>
              <Button variant="contained" onClick={onConnect}>
                {t('account.tradingAccount.finish')}
              </Button>
            </>
          )}
          {(view.kind === 'connected' || view.kind === 'unreachable') && (
            <>
              <Summary summary={view.summary} />
              {view.kind === 'unreachable' && (
                <>
                  <Typography>{t(unreachable.key, unreachable.values)}</Typography>
                  <Button variant="contained" onClick={onConnect}>
                    {t('account.tradingAccount.reconnect')}
                  </Button>
                </>
              )}
              <Button variant="outlined" color="error" onClick={onDisconnect}>
                {t('account.tradingAccount.disconnect')}
              </Button>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
